import React from "react";
import { FrontColors } from "../../../models/FrontColors";
import { FrontSpacing } from "../../../models/FrontSpacing";
import { Flex } from "../../BuildingBlocks/Flex";
import { FontQuicksand } from "../../BuildingBlocks/FontQuicksand";
import { FontSize } from "../../BuildingBlocks/FontSize";
import { MaxWidth } from "../../BuildingBlocks/MaxWidth";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { TextAlign } from "../../BuildingBlocks/TextAlign";
import { TextColor } from "../../BuildingBlocks/TextColor";
import { Width } from "../../BuildingBlocks/Width";
import { WeightSemiBold } from "../../WeightSemiBold/WeightSemiBold";
import { WeightBold } from "../../WeightBold/WeightBold";
import { FrontLink } from "../../FrontLink/FrontLink";
import { FPLandingTitle } from "../Simple/FPLandingTitle/FPLandingTitle";
import { FPSimpleLogo } from "../Simple/FPSimpleLogo/FPSimpleLogo";
import { FPInput } from "../Complex/FPInput/FPInput";
import { FPInputButton } from "../Complex/FPInputButton/FPInputButton";
import { TPublicPropertyReportSignInStatus } from "../../../../../domain/codecs/form/PublicPropertyReportForm";

/**
 * Sign-in for an introducer contact who arrives without an ?introducer_id= in
 * the URL.
 *
 * **When to use:** the property report page's no-introducer branch. The work
 * email's domain is what identifies the firm, and the link is only usable by
 * someone who can read mail at that domain — so the report gets attributed to
 * the right introducer without trusting anything typed into the page.
 *
 * @example
 * <FPPropertyReportSignIn
 *     email={email}
 *     status="idle"
 *     onChangeEmail={setEmail}
 *     onSubmit={submit}
 * />
 */
export const FPPropertyReportSignIn = (props: {
    email: string;
    status: TPublicPropertyReportSignInStatus;
    onChangeEmail: (email: string) => void;
    onSubmit: () => void;
}): JSX.Element => {
    const onSubmit = () => {
        if (props.status !== "submitting" && props.email.trim() !== "") {
            props.onSubmit();
        }
    };

    return <SpacingColumn
        spacing={FrontSpacing.LARGE_1}
        justifyContent="center"
    >
        {/* LOGO */}
        <Flex justifyContent="center">
            <FPSimpleLogo width="95px" />
        </Flex>

        {/* HEADER */}
        <Flex justifyContent="center">
            <MaxWidth width="530px">
                <FPLandingTitle
                    size={60}
                    color={FrontColors.NEUTRAL_18}
                    align="center"
                >
                    {props.status === "link_sent" ? "Check your email." : "The Probate Hub."}
                </FPLandingTitle>
            </MaxWidth>
        </Flex>

        {/* SUBTITLE */}
        <Flex justifyContent="center">
            <MaxWidth width="530px">
                <Flex justifyContent="center">
                    <MaxWidth width="425px">
                        <TextColor color={FrontColors.NEUTRAL_18}>
                            <TextAlign position="center">
                                <FontQuicksand>
                                    <FontSize
                                        size={20}
                                        lineHeight={1.2}
                                    >
                                        <WeightSemiBold>
                                            {props.status === "link_sent" ? (
                                                <><WeightBold>We've sent you a sign-in link.</WeightBold> Open it on this device and <WeightBold>you'll stay signed in for a week</WeightBold>, so you can come straight back here next time.</>
                                            ) : props.status === "domain_not_recognised" ? (
                                                <><WeightBold>We don't recognise that email domain.</WeightBold> Use your work email, or <WeightBold>email the team at <FrontLink href="mailto:hello@sailhomes.co.uk?subject=Property report access">hello@sailhomes.co.uk</FrontLink> and we'll set you up.</WeightBold></>
                                            ) : (
                                                <><WeightBold>Enter your work email to start.</WeightBold> We'll send you a sign-in link, and <WeightBold>your reports are then logged against your firm</WeightBold> automatically.</>
                                            )}
                                        </WeightSemiBold>
                                    </FontSize>
                                </FontQuicksand>
                            </TextAlign>
                        </TextColor>
                    </MaxWidth>
                </Flex>
            </MaxWidth>
        </Flex>

        {/* EMAIL */}
        {props.status !== "link_sent" &&
            <Flex justifyContent="center">
                <Width width="425px">
                    <SpacingRow
                        justifyContent="start"
                        alignItems="center"
                        childSize="1fr min-content"
                    >
                        <FPInput
                            flatEdge={true}
                            placeholder="Your work email"
                            value={props.email}
                            coloursInverted={true}
                            autoFocus={true}
                            displayError={props.status === "domain_not_recognised"}
                            onChange={props.onChangeEmail}
                            onEnter={onSubmit}
                        />

                        <FPInputButton
                            coloursInverted={true}
                            iconName="chevron-right"
                            onClick={onSubmit}
                        />
                    </SpacingRow>
                </Width>
            </Flex>
        }
    </SpacingColumn>;
};
